import React from "react";
import styled from "styled-components";

interface Props {
	children: React.ReactNode;
}

interface State {
	hasError: boolean;
	error?: Error;
	errorInfo?: React.ErrorInfo;
	showDetails: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, showDetails: false };
	}

	static getDerivedStateFromError(error: Error): Partial<State> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error("ErrorBoundary caught an error:", error, errorInfo);
		this.setState({ errorInfo });
	}

	handleReload = () => {
		window.location.reload();
	};

	handleRetry = () => {
		this.setState({ hasError: false, error: undefined, errorInfo: undefined });
	};

	toggleDetails = () => {
		this.setState((prev) => ({ showDetails: !prev.showDetails }));
	};

	render() {
		if (this.state.hasError) {
			const isChunkError =
				this.state.error?.message?.includes("Loading chunk") ||
				this.state.error?.message?.includes("Failed to fetch dynamically imported");

			return (
				<ErrorContainer>
					<ErrorContent>
						<Logo>HEGIAS</Logo>
						<ErrorTitle>Something went wrong</ErrorTitle>
						<ErrorMessage>
							{isChunkError ? (
								<>
									We couldn't load a required component. This usually happens
									after an application update.
									<br />
									<br />
									Please reload the page to continue.
								</>
							) : (
								<>
									An unexpected error occurred. Please try reloading the page, probably the system just received an update.
									<br />
									If the problem persists, contact support.
								</>
							)}
						</ErrorMessage>

						<ButtonGroup>
							<PrimaryButton onClick={this.handleReload}>
								Reload Page
							</PrimaryButton>
							{!isChunkError && (
								<SecondaryButton onClick={this.handleRetry}>
									Try Again
								</SecondaryButton>
							)}
						</ButtonGroup>

						<DetailsSection>
							<DetailsToggle onClick={this.toggleDetails}>
								{this.state.showDetails ? "Hide" : "Show"} Error Details
							</DetailsToggle>
							{this.state.showDetails && (
								<DetailsContent>
									<DetailBlock>
										<DetailLabel>Error Message:</DetailLabel>
										<DetailText>{this.state.error?.message}</DetailText>
									</DetailBlock>
									{this.state.error?.stack && (
										<DetailBlock>
											<DetailLabel>Stack Trace:</DetailLabel>
											<StackTrace>{this.state.error.stack}</StackTrace>
										</DetailBlock>
									)}
								</DetailsContent>
							)}
						</DetailsSection>
					</ErrorContent>
				</ErrorContainer>
			);
		}

		return this.props.children;
	}
}

const ErrorContainer = styled.div`
	min-height: 100vh;
	width: 100%;
	background-color: #ffffff;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 20px;
`;

const ErrorContent = styled.div`
	max-width: 600px;
	width: 100%;
	text-align: center;
`;

const Logo = styled.div`
	font-size: 32px;
	font-weight: 500;
	color: var(--color-primary);
	margin-bottom: 40px;
	letter-spacing: 2px;
`;

const ErrorTitle = styled.h1`
	font-size: 28px;
	font-weight: 500;
	color: #333333;
	margin-bottom: 20px;
`;

const ErrorMessage = styled.p`
	font-size: 16px;
	line-height: 1.6;
	color: #666666;
	margin-bottom: 40px;
`;

const ButtonGroup = styled.div`
	display: flex;
	gap: 12px;
	justify-content: center;
	margin-bottom: 40px;
`;

const PrimaryButton = styled.button`
	background-color: var(--color-primary);
	color: #ffffff;
	border: none;
	border-radius: 4px;
	padding: 12px 32px;
	font-size: 16px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		filter: brightness(110%);
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;

const SecondaryButton = styled.button`
	background-color: #e0e0e0;
	color: #333333;
	border: none;
	border-radius: 4px;
	padding: 12px 32px;
	font-size: 16px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background-color: #d0d0d0;
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
`;

const DetailsSection = styled.div`
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid #e0e0e0;
`;

const DetailsToggle = styled.button`
	background: none;
	border: none;
	color: var(--color-primary);
	font-size: 14px;
	cursor: pointer;
	text-decoration: underline;
	padding: 0;
	margin-bottom: 16px;

	&:hover {
		color: #7a8c2e;
	}
`;

const DetailsContent = styled.div`
	text-align: left;
	background-color: #f8f8f8;
	border-radius: 4px;
	padding: 20px;
`;

const DetailBlock = styled.div`
	margin-bottom: 20px;

	&:last-child {
		margin-bottom: 0;
	}
`;

const DetailLabel = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: var(--color-primary);
	margin-bottom: 8px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

const DetailText = styled.div`
	font-size: 14px;
	color: #333333;
	word-break: break-word;
`;

const StackTrace = styled.pre`
	font-size: 12px;
	color: #666666;
	background-color: #ffffff;
	padding: 12px;
	border-radius: 4px;
	overflow-x: auto;
	white-space: pre-wrap;
	word-wrap: break-word;
	font-family: "Courier New", monospace;
	line-height: 1.4;
`;

export default ErrorBoundary;
